import React, { useState } from 'react';
import Select from 'react-select';
import { selectStyles } from '../utils/selectStyles';
import {
  MONITOR_METRIC_OPTIONS,
  RUN_ACTION_OPTIONS,
  INTERVAL_OPTIONS,
  THROUGHPUT_INTERVAL_OPTIONS,
  METRIC_LABELS,
  ACTION_ICONS,
  defaultThroughputConfig,
} from '../../services/realTimeDeviceService';
import '../assets/AllTabsCommon.css';
import '../assets/StartRemoteDebug.css';
import '../assets/Home.css';
import '../assets/RemoteDebugSessionTabs.css';

/**
 * Shared Monitor / Configure / Run action editor.
 *
 * Props:
 *   actions   : array of configured action objects
 *   onAdd     : (action) => void
 *   onRemove  : (actionId) => void
 *   disabled  : optional; hides the add UI (read-only mode)
 *   variant   : 'start' (default) | 'active'
 *               'active' — renames card to "Active Actions", puts "+ Add Action"
 *               inline in the card header, shows per-row status badges, and
 *               uses per-type edit/delete rules (configure: Update only, no delete;
 *               monitor/run: ghost edit + delete).
 */
function RTActionEditor({ actions = [], onAdd, onRemove, onUpdate, disabled = false, variant = 'start' }) {
  const [openForm, setOpenForm] = useState(null); // null | 'monitor' | 'configure' | 'run'
  const [error, setError] = useState('');
  const [showTypeChooser, setShowTypeChooser] = useState(false);
  const [editingAction, setEditingAction] = useState(null); // action object being edited, or null

  const isActive = variant === 'active';
  const closeForm = () => { setOpenForm(null); setError(''); setShowTypeChooser(false); setEditingAction(null); };

  const openTypeChooser = () => { setError(''); setShowTypeChooser(true); };
  const openFormFromChooser = (type) => { setShowTypeChooser(false); setOpenForm(type); };

  const hasAction = (key) => actions.some((a) => a.key === key);
  const configureAdded = hasAction('configure:celllock');

  const emitAdd = (action) => {
    if (!editingAction && hasAction(action.key)) {
      setError(`${action.label} is already added.`);
      return;
    }
    if (editingAction) {
      onUpdate(editingAction.id, action);
    } else {
      onAdd(action);
    }
    closeForm();
  };

  const typeRowClass = (type) => {
    if (type === 'monitor') return 'rt-action-row-monitor';
    if (type === 'configure') return 'rt-action-row-configure';
    if (type === 'run') return 'rt-action-row-run';
    return '';
  };

  const statusChip = (type) => {
    if (type === 'run') return <span className="rt-chip rt-chip-pending">⏱ Pending</span>;
    return <span className="rt-chip rt-chip-running">↗ Running</span>;
  };

  const actionControls = (a) => {
    if (disabled) return null;
    if (isActive) {
      if (a.type === 'configure') {
        // Configure: Update button only (no delete — cell lock is part of session config)
        return (
          <button
            type="button"
            className="rt-action-update-btn"
            title="Update cell lock configuration"
            onClick={() => { setError(''); setEditingAction(a); setShowTypeChooser(false); setOpenForm('configure'); }}
          >
            ✏️ Update
          </button>
        );
      }
      // Monitor / Run: ghost edit icon + delete
      return (
        <>
          <button type="button" className="rt-action-edit-btn" title="Edit (not yet supported)" disabled>✏️</button>
          <button type="button" className="rt-action-remove" onClick={() => onRemove(a.id)} aria-label={`Remove ${a.label}`}>🗑</button>
        </>
      );
    }
    // Start variant: single ✕ remove
    return (
      <button type="button" className="rt-action-remove" onClick={() => onRemove(a.id)} aria-label={`Remove ${a.label}`}>✕</button>
    );
  };

  const typeChooser = (
    <>
      {!isActive && (
        <p className="rt-text-muted" style={{ marginBottom: 12 }}>
          Add monitoring, configuration, or one-time diagnostic actions.
        </p>
      )}
      <div className="rt-action-type-selector">
        <button type="button" className="rt-action-type-card" onClick={() => { setError(''); isActive ? openFormFromChooser('monitor') : setOpenForm('monitor'); }}>
          <span className="rt-action-type-icon">👁️</span>
          <span className="rt-action-type-title">Monitor</span>
          <span className="rt-action-type-sub">Track metrics at fixed intervals</span>
        </button>
        <button
          type="button"
          className={`rt-action-type-card${configureAdded ? ' disabled' : ''}`}
          onClick={() => { setError(''); isActive ? openFormFromChooser('configure') : setOpenForm('configure'); }}
          title={configureAdded ? 'Cell Lock already configured' : undefined}
        >
          <span className="rt-action-type-icon">⚙️</span>
          <span className="rt-action-type-title">Configure</span>
          <span className="rt-action-type-sub">{configureAdded ? 'Already added' : 'Change device settings'}</span>
        </button>
        <button type="button" className="rt-action-type-card" onClick={() => { setError(''); isActive ? openFormFromChooser('run') : setOpenForm('run'); }}>
          <span className="rt-action-type-icon">⚡</span>
          <span className="rt-action-type-title">Run Action</span>
          <span className="rt-action-type-sub">Execute one-time commands</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="rt-action-editor card rt-card">
      {/* ── Card header ── */}
      <div className="card-header">
        {isActive && !disabled ? (
          <div className="rt-action-header-row">
            <h3 className="card-title">Active Actions <span className="rt-count-badge">{actions.length}</span></h3>
            {(openForm !== null || showTypeChooser)
              ? <button type="button" className="go-back-button" onClick={closeForm}>Cancel</button>
              : <button type="button" className="submit-button" onClick={openTypeChooser}>+ Add Action</button>            }
          </div>
        ) : (
          <>
            <h3 className="card-title">Configured Actions</h3>
            <span className="rt-count-badge">{actions.length}</span>
          </>
        )}
      </div>

      {/* ── Configured action rows ── */}
      <div className={`rt-configured-actions${actions.length === 0 ? ' empty' : ''}`}>
        {actions.length === 0 ? (
          <div className="rt-empty-state">
            <span className="rt-empty-icon">📋</span>
            <p className="rt-empty-title">{isActive ? 'No active actions' : 'No actions configured yet'}</p>
            <p className="rt-empty-hint">
              {disabled
                ? 'This session has no actions.'
                : isActive
                  ? 'Use "+ Add Action" above to add a monitoring or configuration action.'
                  : 'Choose an action type below to add your first action.'}
            </p>
          </div>
        ) : (
          actions.map((a) => (
            <div key={a.id} className={`rt-action-item ${typeRowClass(a.type)}`}>
              <span className={`rt-action-icon ${a.type}`}>{a.icon || ACTION_ICONS[a.type]}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="rt-action-label">{a.label}</div>
                {a.sub && <div className="rt-action-sub">{a.sub}</div>}
              </div>
              {isActive && statusChip(a.type)}
              {actionControls(a)}
            </div>
          ))
        )}
      </div>

      {/* ── Add UI (start variant: below divider; active variant: inline panel toggled from header) ── */}
      {!disabled && (
        <>
          {isActive ? (
            (showTypeChooser || openForm !== null) && (
              <>
                <hr className="rt-action-editor-divider" />
                {error && <div className="error-banner" style={{ marginBottom: 12 }}>{error}</div>}
                {showTypeChooser && openForm === null && typeChooser}
                {openForm !== null && (
                  <div className={`rt-action-form ${openForm}`}>
                    {openForm === 'monitor' && <MonitorForm onSubmit={emitAdd} onCancel={closeForm} />}
                    {openForm === 'configure' && <ConfigureForm onSubmit={emitAdd} onCancel={closeForm} hasAction={hasAction} setError={setError} initialValues={editingAction} isEditing={!!editingAction} />}
                    {openForm === 'run' && <RunForm onSubmit={emitAdd} onCancel={closeForm} />}
                  </div>
                )}
              </>
            )
          ) : (
            <>
              <hr className="rt-action-editor-divider" />
              {error && <div className="error-banner" style={{ marginBottom: 12 }}>{error}</div>}
              {openForm === null ? typeChooser : (
                <div className={`rt-action-form ${openForm}`}>
                  {openForm === 'monitor' && <MonitorForm onSubmit={emitAdd} onCancel={closeForm} />}
                  {openForm === 'configure' && <ConfigureForm onSubmit={emitAdd} onCancel={closeForm} hasAction={hasAction} setError={setError} initialValues={editingAction} isEditing={!!editingAction} />}
                  {openForm === 'run' && <RunForm onSubmit={emitAdd} onCancel={closeForm} />}
                </div>
              )}
            </>
          )}

          {/* Version-increment info banner — active variant only */}
          {isActive && (
            <div className="rt-version-info-banner">
              <span>ℹ️</span>
              <span>
                <strong>Monitor</strong> and <strong>Configure</strong> actions increment the config version (delta sent to device).{' '}
                <strong>Run</strong> actions execute once immediately — version is not incremented.
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Throughput sub-fields ─────────────────────────────────────────────────────
function ThroughputFields({ value, onChange }) {
  const set = (field) => (e) => onChange({ ...value, [field]: e.target.value });
  return (
    <>
      <div className="rt-form-section-label">Throughput Test Configuration</div>
      <div className="rt-throughput-fields">
        <div className="rt-form-field">
          <label>Test Type</label>
          <select value={value.testType} onChange={set('testType')}>
            <option value="ul_dl">Uplink + Downlink</option>
            <option value="ul">Uplink Only</option>
            <option value="dl">Downlink Only</option>
          </select>
        </div>
        <div className="rt-form-field">
          <label>Transport Protocol</label>
          <select value={value.protocol} onChange={set('protocol')}>
            <option value="tcp">TCP</option>
            <option value="udp">UDP</option>
          </select>
        </div>
        <div className="rt-form-field">
          <label>IP Version</label>
          <select value={value.ipVersion} onChange={set('ipVersion')}>
            <option value="ipv4">IPv4</option>
            <option value="ipv6">IPv6</option>
          </select>
        </div>
        <div className="rt-form-field">
          <label>Test Duration (sec) <span className="required-asterisk">*</span></label>
          <input type="number" value={value.durationSec} onChange={set('durationSec')} />
        </div>
        <div className="rt-form-field">
          <label>Test Server Address <span className="required-asterisk">*</span></label>
          <input type="text" placeholder="URL or IP address" value={value.serverAddress} onChange={set('serverAddress')} />
        </div>
        <div className="rt-form-field">
          <label>Port Range Start <span className="required-asterisk">*</span></label>
          <input type="number" placeholder="e.g., 5201" value={value.portRangeStart} onChange={set('portRangeStart')} />
        </div>
        <div className="rt-form-field">
          <label>Port Range End <span className="required-asterisk">*</span></label>
          <input type="number" placeholder="e.g., 5210" value={value.portRangeEnd} onChange={set('portRangeEnd')} />
        </div>
        <div className="rt-form-field">
          <label>Bandwidth (Kbps)</label>
          <input type="number" placeholder="Leave blank for max" value={value.bandwidthKbps} onChange={set('bandwidthKbps')} />
        </div>
        <div className="rt-form-field">
          <label>Parallel Streams</label>
          <input type="number" value={value.parallelStreams} onChange={set('parallelStreams')} />
        </div>
      </div>
    </>
  );
}

// ── Monitor form ──────────────────────────────────────────────────────────────
function MonitorForm({ onSubmit, onCancel }) {
  const [metric, setMetric] = useState(MONITOR_METRIC_OPTIONS[0]);
  const [interval, setIntervalValue] = useState(INTERVAL_OPTIONS[2]); // 30s
  const [maxIterations, setMaxIterations] = useState('');
  const [throughput, setThroughput] = useState(defaultThroughputConfig());

  const isThroughput = metric?.value === 'throughput';
  const intervalOptions = isThroughput ? THROUGHPUT_INTERVAL_OPTIONS : INTERVAL_OPTIONS;

  const handleMetricChange = (opt) => {
    setMetric(opt);
    const next = opt?.value === 'throughput' ? THROUGHPUT_INTERVAL_OPTIONS[0] : INTERVAL_OPTIONS[2];
    setIntervalValue(next);
  };

  const submit = () => {
    const key = `monitor:${metric.value}`;
    const sub = `Every ${interval.value}${maxIterations ? ` · max ${maxIterations}` : ''}`;
    onSubmit({
      id: Date.now(),
      type: 'monitor',
      key,
      label: METRIC_LABELS[metric.value] || metric.label,
      sub,
      icon: ACTION_ICONS.monitor,
      metric: metric.value,
      interval: interval.value,
      maxIterations: maxIterations || null,
      ...(isThroughput ? { throughput } : {}),
    });
  };

  return (
    <>
      <div className="rt-inline-form-title">Configure New Monitor Action</div>
      <div className="rt-form-field">
        <label>Metric to Monitor</label>
        <Select options={MONITOR_METRIC_OPTIONS} value={metric} onChange={handleMetricChange} styles={selectStyles} />
      </div>
      <div className="rt-form-row-2">
        <div className="rt-form-field" style={{ marginBottom: 0 }}>
          <label>Collection Interval</label>
          <Select options={intervalOptions} value={interval} onChange={setIntervalValue} styles={selectStyles} />
        </div>
        <div className="rt-form-field" style={{ marginBottom: 0 }}>
          <label>Max Iterations</label>
          <input type="number" placeholder="Unlimited" value={maxIterations} onChange={(e) => setMaxIterations(e.target.value)} />
        </div>
      </div>
      {isThroughput && <ThroughputFields value={throughput} onChange={setThroughput} />}
      <FormActions onSubmit={submit} onCancel={onCancel} />
    </>
  );
}

// ── Configure form (Cell Lock) ────────────────────────────────────────────────
const emptyLteCell = () => ({ id: Date.now() + Math.floor(performance.now()), pci: '', earfcn: '' });
const emptyNr5gPciCell = () => ({ id: Date.now() + Math.floor(performance.now()), pci: '', arfcn: '', scs: '30', bandMask: '' });

function ConfigureForm({ onSubmit, onCancel, hasAction, setError, initialValues = null, isEditing = false }) {
  const iv = initialValues;
  const [parameter, setParameter]     = useState(iv?.parameter || 'celllock');
  const [rat, setRat] = useState(iv?.rat || 'LTE');
  const [clearRat, setClearRat] = useState(iv?.clearRat || 'LTE');
  const [enforceNoPowerCycle, setEnforceNoPowerCycle] = useState(iv?.enforceNoPowerCycle || false);
  const [lteCells, setLteCells] = useState(iv?.lteCells?.length ? iv.lteCells : [emptyLteCell()]);
  const [nr5gLockType, setNr5gLockType] = useState(iv?.nr5gLockType || 'PCI');
  const [nr5gPciCells, setNr5gPciCells] = useState(iv?.nr5gPciCells?.length ? iv.nr5gPciCells : [emptyNr5gPciCell()]);
  const [nr5gArfcns, setNr5gArfcns] = useState(iv?.nr5gArfcns?.length ? iv.nr5gArfcns : ['']);
  const [deprioritize5G, setDeprioritize5G] = useState(iv?.deprioritize5G ?? true);

  const submit = () => {
    if (parameter === 'deprioritize5g') {
      if (!isEditing && hasAction('configure:deprioritize5g')) {
        setError('Only one Deprioritize 5G configuration is allowed per session.');
        return;
      }
      onSubmit({
        id: Date.now(),
        type: 'configure',
        key: 'configure:deprioritize5g',
        label: 'Deprioritize 5G',
        sub: deprioritize5G ? 'Enable' : 'Disable',
        icon: ACTION_ICONS.configure,
        parameter: 'deprioritize5g',
        deprioritize5G,
      });
      return;
    }

    if (!isEditing && hasAction('configure:celllock')) {
      setError('Only one Cell Lock configuration is allowed per session.');
      return;
    }
    let sub;
    if (rat === 'Clear') sub = `Clear cell lock · ${clearRat}`;
    else if (rat === 'LTE') sub = `LTE · ${lteCells.length} cell${lteCells.length !== 1 ? 's' : ''}`;
    else sub = `NR5G · ${nr5gLockType === 'PCI' ? nr5gPciCells.length : nr5gArfcns.length} cell(s)`;

    onSubmit({
      id: Date.now(),
      type: 'configure',
      key: 'configure:celllock',
      label: 'Cell Lock',
      sub,
      icon: ACTION_ICONS.configure,
      parameter: 'celllock',
      rat,
      enforceNoPowerCycle,
      ...(rat === 'LTE' ? { lteCells } : {}),
      ...(rat === 'NR5G' ? { nr5gLockType, nr5gPciCells, nr5gArfcns } : {}),
      ...(rat === 'Clear' ? { clearRat } : {}),
    });
  };

  return (
    <>
      <div className="rt-inline-form-title">{isEditing ? 'Update Configure' : 'Configure'}</div>

      <div className="rt-form-field">
        <label>Parameter</label>
        <select value={parameter} onChange={(e) => setParameter(e.target.value)}>
          <option value="celllock">Cell Lock List Configuration</option>
          <option value="deprioritize5g">Deprioritize5GCfg</option>
        </select>
      </div>

      {parameter === 'deprioritize5g' && (
        <div className="rt-celllock-zone">
          <div className="rt-celllock-zone-header">
            <span className="rt-celllock-zone-label">Deprioritize 5G</span>
          </div>
          <div className="rt-rat-selector">
            {[true, false].map((val) => (
              <button key={String(val)} type="button"
                className={`rt-rat-button ${deprioritize5G === val ? 'active' : ''}`}
                onClick={() => setDeprioritize5G(val)}
              >
                {val ? 'Enable' : 'Disable'}
              </button>
            ))}
          </div>
          <p className="rt-hint" style={{ marginTop: 8 }}>
            {deprioritize5G
              ? 'Device will deprioritize 5G and prefer LTE.'
              : 'Removes 5G deprioritization; normal RAT selection resumes.'}
          </p>
        </div>
      )}

      {parameter === 'celllock' && (
        <>
          <div className="rt-form-field">
            <label>RAT</label>
            <div className="rt-rat-selector">
              {['LTE', 'NR5G', 'Clear'].map((r) => (
                <button key={r} type="button" className={`rt-rat-button ${rat === r ? 'active' : ''}`} onClick={() => setRat(r)}>
                  {r === 'Clear' ? 'Clear Lock' : r}
                </button>
              ))}
            </div>
          </div>

      {rat === 'LTE' && (
        <div className="rt-celllock-zone">
          <div className="rt-celllock-zone-header">
            <span className="rt-celllock-zone-label">LTE Cell Lock</span>
            <label className="rt-celllock-checkbox">
              <input type="checkbox" checked={enforceNoPowerCycle} onChange={(e) => setEnforceNoPowerCycle(e.target.checked)} />
              Enforce without power cycle
            </label>
          </div>
          {lteCells.map((cell, idx) => (
            <div key={cell.id} className="rt-cell-row-grid">
              <div className="rt-form-field">
                <label>PCI <span className="required-asterisk">*</span> <span style={{ fontWeight: 400, color: '#999' }}>(0–503)</span></label>
                <input type="number" placeholder="e.g. 101" value={cell.pci}
                  onChange={(e) => setLteCells(lteCells.map((c, i) => (i === idx ? { ...c, pci: e.target.value } : c)))} />
              </div>
              <div className="rt-form-field">
                <label>EARFCN <span className="required-asterisk">*</span> <span style={{ fontWeight: 400, color: '#999' }}>(freq)</span></label>
                <input type="number" placeholder="Positive integer" value={cell.earfcn}
                  onChange={(e) => setLteCells(lteCells.map((c, i) => (i === idx ? { ...c, earfcn: e.target.value } : c)))} />
              </div>
              <button type="button" className="rt-cell-remove" onClick={() => setLteCells(lteCells.filter((_, i) => i !== idx))} title="Remove">✕</button>
            </div>
          ))}
          <button type="button" className="rt-add-cell-link" onClick={() => setLteCells([...lteCells, emptyLteCell()])}>+ Add Cell</button>
          <p className="rt-hint">Empty list = clears the LTE cell lock.</p>
        </div>
      )}

      {rat === 'NR5G' && (
        <div className="rt-celllock-zone">
          <div className="rt-celllock-zone-header">
            <span className="rt-celllock-zone-label">NR5G Cell Lock</span>
            <div className="rt-rat-selector">
              {['PCI', 'ARFCN'].map((t) => (
                <button key={t} type="button" className={`rt-rat-button ${nr5gLockType === t ? 'active' : ''}`} onClick={() => setNr5gLockType(t)}>
                  {t === 'PCI' ? 'PCI-based' : 'ARFCN-based'}
                </button>
              ))}
            </div>
          </div>
          {nr5gLockType === 'PCI'
            ? nr5gPciCells.map((cell, idx) => (
                <div key={cell.id} className="rt-cell-row-grid" style={{ gridTemplateColumns: '1fr 1fr 1fr 1fr auto' }}>
                  <div className="rt-form-field">
                    <label>PCI</label>
                    <input type="number" placeholder="PCI" value={cell.pci}
                      onChange={(e) => setNr5gPciCells(nr5gPciCells.map((c, i) => (i === idx ? { ...c, pci: e.target.value } : c)))} />
                  </div>
                  <div className="rt-form-field">
                    <label>ARFCN</label>
                    <input type="number" placeholder="ARFCN" value={cell.arfcn}
                      onChange={(e) => setNr5gPciCells(nr5gPciCells.map((c, i) => (i === idx ? { ...c, arfcn: e.target.value } : c)))} />
                  </div>
                  <div className="rt-form-field">
                    <label>SCS</label>
                    <select value={cell.scs} onChange={(e) => setNr5gPciCells(nr5gPciCells.map((c, i) => (i === idx ? { ...c, scs: e.target.value } : c)))}>
                      {['15', '30', '60', '120', '240'].map((s) => <option key={s} value={s}>{s} kHz</option>)}
                    </select>
                  </div>
                  <div className="rt-form-field">
                    <label>Band Mask</label>
                    <input type="text" placeholder="Band Mask" value={cell.bandMask}
                      onChange={(e) => setNr5gPciCells(nr5gPciCells.map((c, i) => (i === idx ? { ...c, bandMask: e.target.value } : c)))} />
                  </div>
                  <button type="button" className="rt-cell-remove" onClick={() => setNr5gPciCells(nr5gPciCells.filter((_, i) => i !== idx))} title="Remove">✕</button>
                </div>
              ))
            : nr5gArfcns.map((arfcn, idx) => (
                <div key={idx} className="rt-cell-row-grid" style={{ gridTemplateColumns: '1fr auto' }}>
                  <div className="rt-form-field">
                    <label>ARFCN</label>
                    <input type="number" placeholder="ARFCN" value={arfcn}
                      onChange={(e) => setNr5gArfcns(nr5gArfcns.map((a, i) => (i === idx ? e.target.value : a)))} />
                  </div>
                  <button type="button" className="rt-cell-remove" onClick={() => setNr5gArfcns(nr5gArfcns.filter((_, i) => i !== idx))} title="Remove">✕</button>
                </div>
              ))}
          <button type="button" className="rt-add-cell-link"
            onClick={() => (nr5gLockType === 'PCI' ? setNr5gPciCells([...nr5gPciCells, emptyNr5gPciCell()]) : setNr5gArfcns([...nr5gArfcns, '']))}>
            + Add Cell
          </button>
        </div>
      )}

      {rat === 'Clear' && (
        <div className="rt-celllock-zone">
          <div className="rt-celllock-zone-header">
            <span className="rt-celllock-zone-label">Apply to RAT</span>
          </div>
          <div className="rt-rat-selector" style={{ marginBottom: 8 }}>
            {['LTE', 'NR5G'].map((r) => (
              <button key={r} type="button" className={`rt-rat-button ${clearRat === r ? 'active' : ''}`} onClick={() => setClearRat(r)}>
                {r}
              </button>
            ))}
          </div>
          <p className="rt-hint" style={{ margin: 0 }}>Removes all cell lock restrictions for {clearRat}. The device will reconnect to the best available cell.</p>
        </div>
      )}
        </>
      )}

      <FormActions onSubmit={submit} onCancel={onCancel} submitLabel={isEditing ? 'Update' : 'Add Action'} />
    </>
  );
}

// ── Run form ──────────────────────────────────────────────────────────────────
function RunForm({ onSubmit, onCancel }) {
  const [action, setAction] = useState(RUN_ACTION_OPTIONS[0]);
  const [throughput, setThroughput] = useState(defaultThroughputConfig());

  const isThroughput = action?.value === 'throughput';

  const submit = () => {
    onSubmit({
      id: Date.now(),
      type: 'run',
      key: `run:${action.value}`,
      label: METRIC_LABELS[action.value] || action.label,
      sub: 'One-time action',
      icon: ACTION_ICONS.run,
      action: action.value,
      ...(isThroughput ? { throughput } : {}),
    });
  };

  return (
    <>
      <div className="rt-inline-form-title">Configure Run Action</div>
      <div className="rt-form-field">
        <label>Action to Run</label>
        <Select options={RUN_ACTION_OPTIONS} value={action} onChange={setAction} styles={selectStyles} />
      </div>
      <div className="rt-run-note">
        <span>⚡</span>
        <span>Runs <strong>once immediately</strong> when the session starts. Not saved to snapshot — not replayed on device restart.</span>
      </div>
      {isThroughput && <ThroughputFields value={throughput} onChange={setThroughput} />}
      <FormActions onSubmit={submit} onCancel={onCancel} submitLabel="Add Action" />
    </>
  );
}

function FormActions({ onSubmit, onCancel, submitLabel = 'Add Action' }) {
  return (
    <div className="review-actions">
      <button type="button" className="submit-button" onClick={onSubmit}>{submitLabel}</button>
      <button type="button" className="go-back-button" onClick={onCancel}>Cancel</button>
    </div>
  );
}

export default RTActionEditor;
